import './styles/main.scss';
import '@maucworks/ui-components';
import { MapSelector } from './map-selector';
import { ProgressTracker } from './progress-tracker';
import { Viewer3D } from './viewer';

class OnderleggerApp {
    private mapSelector: MapSelector | null = null;
    private progressTracker: ProgressTracker | null = null;
    private viewer: Viewer3D | null = null;
    private currentOnderleggerId: string | null = null;
    
    constructor() {
        this.init();
    }
    
    init(): void {
        // Initialize map selector
        this.mapSelector = new MapSelector('map');
        this.mapSelector.setupSearchBox();
        this.mapSelector.onRadiusChange(() => {
            // Radius updated
        });
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Show initial step
        this.showStep('location');
    }
    
    setupEventListeners(): void {
        // Generate button
        const generateBtn = document.getElementById('btn-generate');
        generateBtn?.addEventListener('click', () => this.handleGenerate());
        
        // Download ZIP button
        const downloadBtn = document.getElementById('btn-download-zip');
        downloadBtn?.addEventListener('click', () => this.handleDownloadZip());
        
        // New onderlegger button
        const newBtn = document.getElementById('btn-new-onderlegger');
        newBtn?.addEventListener('click', () => this.handleNewOnderlegger());
        
        // Reset camera button
        const resetCameraBtn = document.getElementById('btn-reset-camera');
        resetCameraBtn?.addEventListener('click', () => this.viewer?.resetCamera());
        
        // Screenshot button
        const screenshotBtn = document.getElementById('btn-screenshot');
        screenshotBtn?.addEventListener('click', () => this.viewer?.takeScreenshot());
    }
    
    async handleGenerate(): Promise<void> {
        if (!this.mapSelector) return;
        
        const location = this.mapSelector.getSelectedLocation();
        if (!location) {
            alert('Selecteer eerst een locatie op de kaart');
            return;
        }
        
        const radiusSlider = document.getElementById('radius-slider') as HTMLInputElement;
        const presetSelector = document.getElementById('preset-selector') as HTMLSelectElement;
        
        const radius = parseInt(radiusSlider.value);
        const presetName = presetSelector.value;
        
        // Show progress step
        this.showStep('progress');
        
        try {
            // Create onderlegger via API
            const formData = new FormData();
            formData.append('location_name', location.name);
            formData.append('latitude', location.lat.toString());
            formData.append('longitude', location.lng.toString());
            formData.append('radius', radius.toString());
            formData.append('preset_name', presetName);
            
            const response = await fetch('/api/onderleggers', {
                method: 'POST',
                body: formData
            });
            
            if (!response.ok) {
                throw new Error('Failed to create onderlegger');
            }
            
            const data = await response.json();
            this.currentOnderleggerId = data.id;
            
            // Start progress tracking
            this.progressTracker = new ProgressTracker(data.id);
            const success = await this.progressTracker.start();
            
            if (success) {
                // Show result step
                await this.showResult(data.id);
            }
            // If failed, ProgressTracker already showed error UI
            
        } catch (error) {
            console.error('Failed to generate onderlegger:', error);
            alert('Er is een fout opgetreden bij het genereren van de onderlegger');
            this.showStep('location');
        }
    }
    
    async showResult(onderleggerId: string): Promise<void> {
        // Fetch onderlegger details
        const response = await fetch(`/api/onderleggers/${onderleggerId}`);
        const data = await response.json();
        
        // Update result panel
        const resultLocation = document.getElementById('result-location');
        const resultRadius = document.getElementById('result-radius');
        const resultPreset = document.getElementById('result-preset');
        
        if (resultLocation) resultLocation.textContent = data.location_name;
        if (resultRadius) resultRadius.textContent = data.radius.toString();
        if (resultPreset) resultPreset.textContent = data.preset_name;
        
        // Show result step
        this.showStep('result');
        
        // Initialize 3D viewer
        this.viewer = new Viewer3D('viewer-canvas');
        
        // Try to load GLB model, show placeholder if not available
        const glbUrl = `/api/onderleggers/${onderleggerId}/files/glb`;
        try {
            await this.viewer.loadGLB(glbUrl);
        } catch (error) {
            console.warn('GLB file not available:', error);
            this.viewer.showPlaceholderMessage('3D model niet beschikbaar');
        }
    }
    
    handleDownloadZip(): void {
        if (!this.currentOnderleggerId) return;
        
        // Trigger download
        window.location.href = `/api/onderleggers/${this.currentOnderleggerId}/download`;
    }
    
    handleNewOnderlegger(): void {
        // Reset state
        this.currentOnderleggerId = null;
        
        // Dispose viewer if exists
        if (this.viewer) {
            this.viewer.dispose();
            this.viewer = null;
        }
        
        // Show location step
        this.showStep('location');
    }
    
    showStep(stepName: string): void {
        const steps = ['location', 'progress', 'result'];
        steps.forEach(name => {
            const step = document.getElementById(`step-${name}`);
            if (step) {
                step.classList.toggle('active', name === stepName);
            }
        });
    }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new OnderleggerApp();
});
